import { supabase } from '../lib/supabase';

export const biometricService = {
  // Check if WebAuthn is supported
  isSupported() {
    return window?.PublicKeyCredential !== undefined && 
           navigator?.credentials !== undefined;
  },

  // Check if platform authenticator (biometric) is available
  async isPlatformAuthenticatorAvailable() {
    if (!this.isSupported()) return false;
    try {
      return await PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch (error) {
      console.error('Platform authenticator check failed:', error);
      return false;
    }
  },

  // Register biometric credential
  async registerBiometric(deviceName) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Generate challenge from server (in production, this should come from backend)
      const challenge = new Uint8Array(32);
      crypto?.getRandomValues(challenge);

      const publicKeyCredentialCreationOptions = {
        challenge: challenge,
        rp: {
          name: 'TriRail StablePay',
          id: window?.location?.hostname
        },
        user: {
          id: new TextEncoder()?.encode(user?.id),
          name: user?.email,
          displayName: user?.user_metadata?.full_name || user?.email
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' },  // ES256
          { alg: -257, type: 'public-key' } // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform', // Use platform authenticator (biometric)
          userVerification: 'required',
          requireResidentKey: false
        },
        timeout: 60000,
        attestation: 'none'
      };

      const credential = await navigator?.credentials?.create({
        publicKey: publicKeyCredentialCreationOptions
      });

      if (!credential) throw new Error('Failed to create credential');

      // Store credential in database
      const credentialData = {
        credential_id: this.arrayBufferToBase64(credential?.rawId),
        public_key: this.arrayBufferToBase64(credential?.response?.getPublicKey()),
        device_name: deviceName,
        authenticator_attachment: credential?.authenticatorAttachment || 'platform'
      };

      const { data, error } = await supabase
        ?.from('user_2fa_methods')
        ?.insert({
          user_id: user?.id,
          method_type: 'biometric',
          is_enabled: true,
          device_name: deviceName,
          secret_key: JSON.stringify(credentialData)
        })
        ?.select()
        ?.single();

      if (error) throw error;

      // Log security event
      await this.logSecurityEvent('method_added', true, { method_type: 'biometric' });

      return {
        id: data?.id,
        methodType: data?.method_type,
        deviceName: data?.device_name,
        credentialId: credentialData?.credential_id
      };
    } catch (error) {
      console.error('Biometric registration failed:', error);
      throw error;
    }
  },

  // Verify biometric credential
  async verifyBiometric(methodId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get stored credential
      const { data: method, error } = await supabase
        ?.from('user_2fa_methods')
        ?.select('*')
        ?.eq('id', methodId)
        ?.eq('user_id', user?.id)
        ?.eq('method_type', 'biometric')
        ?.single();

      if (error || !method) throw new Error('Biometric method not found');

      const credentialData = JSON.parse(method?.secret_key);

      // Generate challenge
      const challenge = new Uint8Array(32);
      crypto?.getRandomValues(challenge);

      const publicKeyCredentialRequestOptions = {
        challenge: challenge,
        allowCredentials: [{
          id: this.base64ToArrayBuffer(credentialData?.credential_id),
          type: 'public-key',
          transports: ['internal']
        }],
        userVerification: 'required',
        timeout: 60000
      };

      const assertion = await navigator?.credentials?.get({
        publicKey: publicKeyCredentialRequestOptions
      });

      if (!assertion) throw new Error('Biometric verification failed');

      // Update last used timestamp
      await supabase
        ?.from('user_2fa_methods')
        ?.update({ last_used_at: new Date()?.toISOString() })
        ?.eq('id', methodId);

      // Log security event
      await this.logSecurityEvent('login_success', true, { method_type: 'biometric' });

      return { success: true };
    } catch (error) {
      console.error('Biometric verification failed:', error);
      await this.logSecurityEvent('login_failed', false, { 
        method_type: 'biometric',
        error: error?.message 
      });
      throw error;
    }
  },

  // Get user's biometric methods
  async getBiometricMethods() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('user_2fa_methods')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.eq('method_type', 'biometric')
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(method => ({
        id: method?.id,
        deviceName: method?.device_name,
        isEnabled: method?.is_enabled,
        lastUsedAt: method?.last_used_at,
        createdAt: method?.created_at
      }));
    } catch (error) {
      console.error('Failed to get biometric methods:', error);
      return [];
    }
  },

  // Remove biometric method
  async removeBiometric(methodId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        ?.from('user_2fa_methods')
        ?.delete()
        ?.eq('id', methodId)
        ?.eq('user_id', user?.id)
        ?.eq('method_type', 'biometric');

      if (error) throw error;

      await this.logSecurityEvent('method_removed', true, { method_type: 'biometric' });
    } catch (error) {
      console.error('Failed to remove biometric method:', error);
      throw error;
    }
  },

  // Helper: Convert ArrayBuffer to Base64
  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes?.byteLength; i++) {
      binary += String.fromCharCode(bytes?.[i]);
    }
    return window?.btoa(binary);
  },

  // Helper: Convert Base64 to ArrayBuffer
  base64ToArrayBuffer(base64) {
    let binary = window?.atob(base64);
    const bytes = new Uint8Array(binary?.length);
    for (let i = 0; i < binary?.length; i++) {
      bytes[i] = binary?.charCodeAt(i);
    }
    return bytes?.buffer;
  },

  // Log security event
  async logSecurityEvent(eventType, success, details = {}) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return;

    try {
      await supabase?.from('security_activity')?.insert({
        user_id: user?.id,
        event_type: eventType,
        success: success,
        details: details,
        device_info: navigator?.userAgent || 'Unknown',
        ip_address: null,
        location: null
      });
    } catch (error) {
      console.error('Failed to log security event:', error);
    }
  }
};

export default biometricService;